import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin } from 'rxjs';
import Swal from 'sweetalert2';
import { getFirebaseBackend } from 'src/app/authUtils';
import { CallbackService } from 'src/app/services/callback/callback.service';
import { CampaignsService } from 'src/app/services/campaigns/campaigns.service';
import { LoaderService } from 'src/app/services/loader/loader.service';
import { UserService } from 'src/app/services/user/user.service';

@Component({
	selector: 'app-reschedules',
	templateUrl: './reschedules.component.html',
	styleUrls: ['./reschedules.component.scss'],
})
export class ReschedulesComponent implements OnInit {
	user: any;
	isLoading = false;
	reschedules: any[] = [];
	selectedReschedule: any = null;
	newDate: string = '';
	sendNotification: boolean = false;

	@ViewChild('rescheduleModal') rescheduleModal: any;

	constructor(
		private callbackSrv: CallbackService,
		private campaignsSrv: CampaignsService,
		private userSrv: UserService,
		private router: Router,
		private route: ActivatedRoute,
		private loaderSrv: LoaderService,
		private modalSrv: NgbModal,
	) {
		this.user = getFirebaseBackend()?.getAuthenticatedUser();
	}

	ngOnInit(): void {
		this.loaderSrv.loading$.subscribe((loading) => {
			this.isLoading = loading;
		});
		this.userSrv.saveAction('Reschedules View');

		if (!this.user || !this.user.IdUser) {
			console.error('No user logged in or invalid UserId');
			return;
		}

		this.loadReschedules();
	}

	goBack(): void {
		this.router.navigate(['/']);
	}

	loadReschedules(): void {
		this.loaderSrv.show();

		this.campaignsSrv.getCallerCampaigns(this.user.IdUser).subscribe({
			next: (res: any) => {
				let campaigns = res?.body ?? res?.data ?? res ?? [];

				const campaignId = this.route.snapshot.queryParamMap.get('campaign_id');
				if (campaignId) {
					campaigns = campaigns.filter((campaign: any) => String(campaign.id) === String(campaignId));
				}

				if (!campaigns.length) {
					this.reschedules = [];
					this.loaderSrv.hide();
					return;
				}

				const requests = campaigns.map((campaign: any) => this.callbackSrv.getPendingReschedules(campaign.id));

				forkJoin(requests).subscribe({
					next: (responses: any[]) => {
						const allReschedules: any[] = [];

						responses.forEach((response: any, index: number) => {
							const items = response?.body ?? response?.data ?? response ?? [];
							const campaign = campaigns[index];

							items.forEach((item: any) => {
								allReschedules.push({
									...item,
									campaign_id: campaign.id,
									campaign_name: campaign.name,
									campaign_alias: campaign.alias,
								});
							});
						});

						this.reschedules = allReschedules;
						this.loaderSrv.hide();
					},
					error: (err) => {
						console.error('Error loading reschedules:', err);
						this.reschedules = [];
						this.loaderSrv.hide();
					},
				});
			},
			error: (err) => {
				console.error('Error loading campaigns:', err);
				this.loaderSrv.hide();
			},
		});
	}

	formatPhone(phone: string): string {
		if (!phone) return '';

		const digits = phone.replace(/\D/g, '');
		if (digits.length === 10) {
			return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
		}

		return phone;
	}

	onPhoneClick(phone: string): void {
		this.userSrv.saveAction('Start Call');

		this.userSrv.startCall({
			campaign: this.selectedReschedule?.campaign_id,
			source_view: 'reschedule',
			primary_phone: phone,
			first_name: this.selectedReschedule?.first_name,
			last_name: this.selectedReschedule?.last_name,
		});
	}

	onActivate(event: any): void {
		if (event.type === 'click') {
			this.openRescheduleModal(event.row);
		}
	}

	openRescheduleModal(row: any): void {
		this.selectedReschedule = row;
		this.newDate = row.date_generated || '';
		this.sendNotification = false;
		this.modalSrv.open(this.rescheduleModal, {
			size: 'lg',
			scrollable: true,
			centered: true,
			backdrop: 'static',
		});
	}

	confirmReschedule(modal: any): void {
		if (!this.selectedReschedule || !this.newDate) {
			Swal.fire('Notice', 'Please select a new date', 'info');
			return;
		}

		Swal.fire({
			title: 'Are you sure?',
			text: 'This lead will be rescheduled to the selected date',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Yes, reschedule it!',
			cancelButtonText: 'Cancel',
		}).then((result) => {
			if (result.isConfirmed) {
				this.loaderSrv.show();

				this.callbackSrv.rescheduleQA(this.selectedReschedule, this.newDate, this.user, this.sendNotification).subscribe({
					next: () => {
						this.loaderSrv.hide();
						modal.close();
						Swal.fire('Success', 'The lead was rescheduled successfully', 'success').then(() => {
							this.loadReschedules();
						});
					},
					error: (err) => {
						this.loaderSrv.hide();
						console.error('Error rescheduling lead:', err);
						Swal.fire('Error', 'The lead could not be rescheduled', 'error');
					},
				});
			}
		});
	}
}
